import React, { useState, useRef } from 'react'
import ReactCrop from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import { PurpleButton } from 'components/shared/buttons/Button'
import Button from 'components/shared/buttons/Button'
import styles from './team-logo-cropper.module.scss'

export default function TeamLogoCropper({ image, onSave, onCancel }) {
    const [crop, setCrop] = useState()
    const [completedCrop, setCompletedCrop] = useState()
    const [loading, setLoading] = useState(false)
    const imgRef = useRef(null)

    const onImageLoad = e => {
        const { width, height } = e.currentTarget
        // Set initial crop to center of image with 1:1 aspect ratio (square)
        const cropSize = Math.min(width, height) * 0.8
        const crop = {
            unit: 'px',
            width: cropSize,
            height: cropSize,
            x: (width - cropSize) / 2,
            y: (height - cropSize) / 2,
        }
        setCrop(crop)
    }

    const getCroppedImg = async () => {
        const image = imgRef.current
        const crop = completedCrop

        if (!image || !crop || !crop.width || !crop.height) {
            throw new Error('Crop area not properly defined')
        }

        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        if (!ctx) {
            throw new Error('No 2d context')
        }

        const scaleX = image.naturalWidth / image.width
        const scaleY = image.naturalHeight / image.height

        // Set canvas size for logo (200x200 for high quality)
        canvas.width = 200
        canvas.height = 200

        // Fill with white background first
        ctx.fillStyle = '#FFFFFF'
        ctx.fillRect(0, 0, canvas.width, canvas.height)

        // Set canvas rendering settings
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'

        // Draw cropped image
        ctx.drawImage(
            image,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            canvas.width,
            canvas.height,
        )

        return new Promise((resolve, reject) => {
            canvas.toBlob(blob => {
                if (!blob) {
                    reject(new Error('Canvas is empty'))
                    return
                }
                resolve(blob)
            }, 'image/png')
        })
    }

    const handleSave = async () => {
        setLoading(true)
        try {
            const croppedBlob = await getCroppedImg()
            await onSave(croppedBlob)
        } catch (e) {
            console.error('Failed to crop image:', e)
            alert('Failed to crop image. Please try again.')
        }
        setLoading(false)
    }

    return (
        <div className={styles.cropperOverlay}>
            <div className={styles.cropperModal}>
                <h3>Crop Logo Image</h3>
                <div className={styles.cropperContainer}>
                    <ReactCrop
                        crop={crop}
                        onChange={(_, percentCrop) => setCrop(percentCrop)}
                        onComplete={c => setCompletedCrop(c)}
                        aspect={1}
                        circularCrop
                        keepSelection
                    >
                        <img
                            ref={imgRef}
                            src={image}
                            alt="Crop me"
                            onLoad={onImageLoad}
                            style={{ maxWidth: '100%', maxHeight: '400px' }}
                        />
                    </ReactCrop>
                </div>
                <div className={styles.actions}>
                    <Button text="Cancel" onClick={onCancel} />
                    <PurpleButton
                        text={loading ? 'Saving...' : 'Save Logo'}
                        onClick={handleSave}
                        disabled={loading || !completedCrop}
                    />
                </div>
            </div>
        </div>
    )
}
